<script lang="ts" setup>
const props = defineProps({
  alignment: {
    default: 'center',
    type: String as PropType<LayoutBarAlignment>,
  },
  closingId: {
    default: '',
    type: String,
  },
  color: {
    default: 'indigo',
    type: String as PropType<LayoutBarColor>,
  },
  hasClose: {
    default: false,
    type: Boolean,
  },
  hideAtBottom: {
    default: true,
    type: Boolean,
  },
})

const emit = defineEmits<{
  close: []
}>()

const closed = ref(true)
const pageBottomReached = ref(false)

const isVisible = computed(() => {
  if (props.hideAtBottom) {
    return !pageBottomReached.value && !closed.value
  }

  return !closed.value
})

onBeforeMount(() => {
  if (import.meta.client) {
    if (props.hasClose && props.closingId) {
      closed.value = localStorage.getItem(props.closingId) === 'true'
    }
    else {
      closed.value = false
    }

    window.addEventListener('scroll', onScroll)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})

function onClose() {
  if (props.closingId) {
    localStorage.setItem(props.closingId, 'true')
  }

  closed.value = true

  emit('close')
}

function onScroll() {
  pageBottomReached.value = window.innerHeight + window.scrollY >= document.body.offsetHeight
}

defineExpose({ onClose })
</script>

<template>
  <div
    v-show="isVisible"
    class="fixed inset-x-0 bottom-0 z-50"
  >
    <div
      class="flex w-full items-center border-t p-5 pb-4"
      :class="{
        'justify-start': alignment === 'left',
        'justify-center': alignment === 'center',
        'justify-end': alignment === 'right',
        'border-indigo-900 bg-indigo-800 text-white': color === 'indigo',
        'border-red-800 bg-red-700 text-white': color === 'red',
        'border-gray-200 bg-gray-100 text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100': color === 'white',
      }"
    >
      <slot
        :closed="closed"
        :on-close="onClose"
        :page-bottom-reached="pageBottomReached"
      />

      <UIcon
        v-if="hasClose"
        class="absolute right-4 top-4 z-50 size-5 cursor-pointer text-white hover:text-red-600"
        :name="getIcon('close')"
        size="lg"
        @click="onClose"
      />
    </div>
  </div>
</template>
